import { API_URL } from '@/lib/apiConfig';
import { useEffect, useRef, useState, useCallback } from 'react';
import { isNativeApp } from '@/lib/native';

const BUILD_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY || '';
const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
// Slow mobile connections can take several seconds to fetch Cloudflare's script.
const LOAD_TIMEOUT_MS = 15000;

export function useTurnstile() {
  const [token, setToken] = useState(null);
  const [failed, setFailed] = useState(false);
  const [errorCode, setErrorCode] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const [config, setConfig] = useState({
    loading: !isNativeApp(),
    enabled: !!BUILD_SITE_KEY && !isNativeApp(),
    siteKey: BUILD_SITE_KEY,
  });
  const widgetId = useRef(null);
  const containerRef = useRef(null);
  const tokenWaiter = useRef(null);

  useEffect(() => {
    if (isNativeApp()) {
      setConfig({ loading: false, enabled: false, siteKey: '' });
      return;
    }

    let mounted = true;
    fetch(`${API_URL}/auth/turnstile`)
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error('Failed to load captcha config'))))
      .then((data) => {
        if (!mounted) return;
        const siteKey = data.siteKey || BUILD_SITE_KEY;
        setConfig({
          loading: false,
          enabled: !!data.enabled && !!siteKey,
          siteKey,
        });
      })
      .catch(() => {
        if (!mounted) return;
        setConfig({
          loading: false,
          enabled: !!BUILD_SITE_KEY,
          siteKey: BUILD_SITE_KEY,
        });
      });

    return () => { mounted = false; };
  }, []);

  const reset = useCallback(() => {
    setToken(null);
    setFailed(false);
    setErrorCode(null);
    tokenWaiter.current = null;
    if (widgetId.current !== null && window.turnstile) {
      window.turnstile.reset(widgetId.current);
    }
  }, []);

  // Reloads the check without a page refresh, including re-fetching a script that never loaded.
  const retry = useCallback(() => {
    if (widgetId.current !== null && window.turnstile) {
      reset();
      return;
    }
    setToken(null);
    setFailed(false);
    setErrorCode(null);
    setAttempt((value) => value + 1);
  }, [reset]);

  const getToken = useCallback(() => new Promise((resolve, reject) => {
    if (token) {
      resolve(token);
      return;
    }
    if (!config.enabled) {
      resolve(null);
      return;
    }
    if (widgetId.current === null || !window.turnstile) {
      retry();
      reject(new Error('Security check is still loading. Please wait a moment and try again.'));
      return;
    }

    const timeoutId = window.setTimeout(() => {
      if (tokenWaiter.current?.reject === reject) tokenWaiter.current = null;
      reject(new Error('Security check timed out. Please try again.'));
    }, 15000);

    tokenWaiter.current = {
      resolve: (value) => {
        window.clearTimeout(timeoutId);
        tokenWaiter.current = null;
        resolve(value);
      },
      reject: (error) => {
        window.clearTimeout(timeoutId);
        tokenWaiter.current = null;
        reject(error);
      },
    };

    try {
      if (failed) {
        setFailed(false);
        setErrorCode(null);
        window.turnstile.reset(widgetId.current);
      }
      window.turnstile.execute(widgetId.current);
    } catch {
      tokenWaiter.current = null;
      window.clearTimeout(timeoutId);
      reject(new Error('Security check could not start. Please try again.'));
    }
  }), [token, config.enabled, failed, retry]);

  return {
    token,
    setToken,
    reset,
    retry,
    attempt,
    getToken,
    containerRef,
    widgetId,
    tokenWaiter,
    loading: config.loading,
    enabled: config.enabled,
    failed,
    errorCode,
    setErrorCode,
    siteKey: config.siteKey,
    setFailed,
  };
}

export default function Turnstile({ turnstile }) {
  useEffect(() => {
    if (turnstile.loading || !turnstile.enabled || !turnstile.siteKey || !turnstile.containerRef.current) return;

    let mounted = true;

    const timeoutId = setTimeout(() => {
      if (mounted && turnstile.widgetId.current === null) {
        turnstile.setFailed(true);
      }
    }, LOAD_TIMEOUT_MS);

    const render = () => {
      if (!mounted || !window.turnstile || !turnstile.containerRef.current) return;
      if (turnstile.widgetId.current !== null) return;

      turnstile.widgetId.current = window.turnstile.render(turnstile.containerRef.current, {
        sitekey: turnstile.siteKey,
        callback: (t) => {
          if (!mounted) return;
          turnstile.setFailed(false);
          turnstile.setErrorCode(null);
          turnstile.setToken(t);
          turnstile.tokenWaiter.current?.resolve(t);
        },
        'expired-callback': () => { if (mounted) turnstile.setToken(null); },
        'error-callback': (code) => {
          if (!mounted) return;
          turnstile.setFailed(true);
          turnstile.setErrorCode(code || null);
          turnstile.tokenWaiter.current?.reject(new Error('Security check failed. Please try again.'));
        },
      });

      if (turnstile.widgetId.current !== null) {
        clearTimeout(timeoutId);
        // A script that arrived after the timeout still recovers the check.
        turnstile.setFailed(false);
      }
    };

    if (window.turnstile) {
      render();
    } else {
      let script = document.querySelector('script[src*="challenges.cloudflare.com/turnstile"]');
      if (script?.dataset.failed) {
        script.remove();
        script = null;
      }
      if (!script) {
        script = document.createElement('script');
        script.src = SCRIPT_URL;
        script.async = true;
        script.onerror = () => {
          script.dataset.failed = 'true';
          if (mounted) turnstile.setFailed(true);
        };
        document.head.appendChild(script);
      }
      script.addEventListener('load', render);
    }

    return () => {
      mounted = false;
      clearTimeout(timeoutId);
      if (turnstile.widgetId.current !== null && window.turnstile) {
        window.turnstile.remove(turnstile.widgetId.current);
        turnstile.widgetId.current = null;
      }
    };
  }, [turnstile.loading, turnstile.enabled, turnstile.siteKey, turnstile.attempt]);

  if (turnstile.loading) {
    return <div className="h-[65px] rounded-lg bg-slate-50 border border-slate-200 animate-pulse" />;
  }

  if (!turnstile.enabled) return null;

  // Keep the widget container mounted so Cloudflare's own retries can still succeed.
  return (
    <div className="space-y-2">
      <div ref={turnstile.containerRef} className="flex justify-center" />
      {turnstile.failed && (
        <div role="alert" className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          <span>
            Security check could not load{turnstile.errorCode ? ` (code ${turnstile.errorCode})` : ''}. Check your connection or any content blockers.
          </span>
          <button type="button" onClick={turnstile.retry} className="rounded-md border border-red-300 bg-white px-2.5 py-1 text-xs font-medium text-red-700 hover:bg-red-100">
            Try again
          </button>
        </div>
      )}
    </div>
  );
}
